import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, MessageCircle, Play, CheckCircle2, XCircle, Clock, Send, MessageSquare, Radar } from "lucide-react";
import { toast } from "sonner";

export default function NotificationsPage() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [channel, setChannel] = useState("sms");
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState("Test alert from HARDIK MEDITECH OPS CONTROL.");
  const [sending, setSending] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggeringUrgent, setTriggeringUrgent] = useState(false);

  const load = () => {
    api.get("/notifications/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/notifications/log?limit=10").then((r) => setLog(r.data.log || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!to) return toast.error("Enter recipient");
    setSending(true);
    try {
      const { data } = await api.post("/notifications/test", { channel, to, message: msg });
      if (data.ok) toast.success(`${channel} sent: ${data.info}`);
      else toast.error(`${channel} failed: ${data.info}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  const triggerDigest = async () => {
    setTriggering(true);
    try {
      await api.post("/notifications/trigger-digest");
      toast.success("Digest dispatch queued — check log in a moment.");
      setTimeout(load, 3000);
    } catch (e) {
      toast.error("Trigger failed");
    } finally {
      setTriggering(false);
    }
  };

  const triggerUrgent = async () => {
    setTriggeringUrgent(true);
    try {
      await api.post("/notifications/trigger-urgent-scan");
      toast.success("Urgent scan queued — critical items will be pushed via SMS/WhatsApp");
      setTimeout(load, 3000);
    } catch (e) {
      toast.error("Trigger failed");
    } finally {
      setTriggeringUrgent(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="notifications-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Alerts</div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">Notification Center</h1>
        <p className="text-sm text-slate-500 mt-1">Configure email & WhatsApp alert delivery, and view the daily digest log.</p>
      </div>

      {/* Provider status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ProviderCard
          icon={Mail}
          name="Email (SendGrid)"
          configured={status?.email?.configured}
          detail={status?.email?.sender || "Add SENDGRID_API_KEY + SENDGRID_SENDER"}
        />
        <ProviderCard
          icon={MessageSquare}
          name="SMS (Twilio)"
          configured={status?.sms?.configured}
          detail={status?.sms?.from ? `From: ${status.sms.from}` : "Add TWILIO_ACCOUNT_SID + TWILIO_AUTH_TOKEN + TWILIO_SMS_FROM"}
        />
        <ProviderCard
          icon={MessageCircle}
          name="WhatsApp (Twilio)"
          configured={status?.whatsapp?.configured}
          detail={status?.whatsapp?.from ? `From: ${status.whatsapp.from}` : "Add TWILIO_WHATSAPP_FROM (whatsapp:+xxx)"}
        />
        <Card className="border-slate-200" data-testid="scheduler-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-600" />
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Schedules</div>
            </div>
            <div className="mt-3 space-y-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Daily Digest</div>
                <div className="text-sm font-semibold text-slate-900">{status?.next_digest_run ? new Date(status.next_digest_run).toLocaleString() : "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Urgent Scan</div>
                <div className="text-sm font-semibold text-slate-900">{status?.next_urgent_scan ? new Date(status.next_urgent_scan).toLocaleTimeString() : "—"}</div>
              </div>
            </div>
            <Button
              onClick={triggerDigest}
              disabled={triggering}
              variant="outline"
              className="w-full mt-3"
              data-testid="trigger-digest-button"
            >
              <Play className="h-4 w-4 mr-2" /> Run Digest Now
            </Button>
            <Button
              onClick={triggerUrgent}
              disabled={triggeringUrgent}
              variant="outline"
              className="w-full mt-2"
              data-testid="trigger-urgent-button"
            >
              <Radar className="h-4 w-4 mr-2" /> Run Urgent Scan Now
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Test send */}
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Send a Test Message</div>
          <div className="text-base font-semibold text-slate-900 mb-4">Verify your provider setup</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-slate-700 text-xs font-semibold uppercase tracking-wider">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="mt-1" data-testid="test-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">SMS (Twilio)</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp (Twilio)</SelectItem>
                  <SelectItem value="email">Email (SendGrid)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Label className="text-slate-700 text-xs font-semibold uppercase tracking-wider">To</Label>
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder={channel === "email" ? "you@example.com" : "+919999999999"}
                className="mt-1"
                data-testid="test-to"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-700 text-xs font-semibold uppercase tracking-wider">Message</Label>
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} className="mt-1" data-testid="test-message" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={sendTest} disabled={sending || !to} className="bg-slate-900 hover:bg-slate-800" data-testid="test-send-button">
              <Send className="h-4 w-4 mr-2" /> Send Test
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Log */}
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Recent Dispatch Log</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-2">When</th>
                  <th className="text-left py-2 px-2">Alerts</th>
                  <th className="text-left py-2 px-2">Emails</th>
                  <th className="text-left py-2 px-2">WhatsApp</th>
                </tr>
              </thead>
              <tbody>
                {log.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-slate-500">No dispatches yet</td></tr>
                )}
                {log.map((l) => (
                  <tr key={l._id} className="border-b border-slate-100">
                    <td className="py-2 px-2 text-slate-600">{new Date(l.generated_at).toLocaleString()}</td>
                    <td className="py-2 px-2 font-semibold text-slate-900">{l.total_alerts}</td>
                    <td className="py-2 px-2 text-slate-700">
                      {(l.email_results || []).length} sent
                      <span className="text-[10px] text-slate-500 ml-1">
                        ({(l.email_results || []).filter((x) => x.ok).length} ok)
                      </span>
                    </td>
                    <td className="py-2 px-2 text-slate-700">
                      {(l.whatsapp_results || []).length} sent
                      <span className="text-[10px] text-slate-500 ml-1">
                        ({(l.whatsapp_results || []).filter((x) => x.ok).length} ok)
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ProviderCard({ icon: Icon, name, configured, detail }) {
  return (
    <Card className={`border-slate-200 ${configured ? "" : "bg-slate-50/50"}`} data-testid={`provider-${name.split(" ")[0].toLowerCase()}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${configured ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
              <Icon className="h-4.5 w-4.5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{name}</div>
              <div className="text-base font-semibold text-slate-900">
                {configured ? "Connected" : "Not configured"}
              </div>
            </div>
          </div>
          <Badge variant="outline" className={configured ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}>
            {configured ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
            {configured ? "OK" : "OFF"}
          </Badge>
        </div>
        <div className="mt-3 text-xs text-slate-600">{detail}</div>
      </CardContent>
    </Card>
  );
}
